import { Injectable } from '@angular/core';
import { delay, map, of, type Observable } from 'rxjs';

import type { AddTimelineEventPayload, TimelineEventsClient } from '../tokens/timeline-events-client.token';
import type { TimelineEvent, TimelineEventType } from '../../features/variance-tracker/components/variance-supporting-data/models/timeline-event.model';

const addedEventsByCaseId = new Map<number, TimelineEvent[]>();
let nextEventId = 10_000;

@Injectable({ providedIn: 'root' })
export class TimelineEventsMockService implements TimelineEventsClient {
  getTimelineEvents(caseId: number): Observable<readonly TimelineEvent[]> {
    const base = generateMockTimelineEvents(caseId);
    const added = addedEventsByCaseId.get(caseId) ?? [];
    const merged = [...added, ...base];
    return of(merged).pipe(
      map((events) => [...events].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())),
      delay(200)
    );
  }

  addEvent(caseId: number, payload: AddTimelineEventPayload): void {
    const id = nextEventId++;
    const event: TimelineEvent = {
      id,
      message: payload.message,
      actorUserId: 1,
      createdAt: new Date(),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 1, displayName: 'Brooklyn Benson' },
      timelineEventTarget: { id, timelineEventId: id, caseInvestigationId: caseId },
      eventType: payload.eventType,
      eventSource: 'User',
      incidentRowId: payload.incidentRowId ?? null,
    };
    const list = addedEventsByCaseId.get(caseId) ?? [];
    list.push(event);
    addedEventsByCaseId.set(caseId, list);
  }
}

function generateMockTimelineEvents(caseInvestigationId: number): readonly TimelineEvent[] {
  const events: readonly TimelineEvent[] = [
    {
      id: 1,
      message: 'New email response from email@example.com',
      actorUserId: 1,
      createdAt: new Date('2025-02-13T08:16:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 1, displayName: 'Brooklyn Benson' },
      timelineEventTarget: { id: 1, timelineEventId: 1, caseInvestigationId },
      eventType: 'EmailResponse' as TimelineEventType,
      eventSource: 'User',
    },
    {
      id: 2,
      message: 'Incident sent as email to fer@example.com',
      actorUserId: 1,
      createdAt: new Date('2025-02-13T08:15:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 1, displayName: 'Brooklyn Benson' },
      timelineEventTarget: { id: 2, timelineEventId: 2, caseInvestigationId },
      eventType: 'EmailNotification' as TimelineEventType,
      eventSource: 'User',
    },
    {
      id: 3,
      message: 'New Feature Comparison added as incident',
      actorUserId: 1,
      createdAt: new Date('2025-03-01T09:15:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 1, displayName: 'Brooklyn Benson' },
      timelineEventTarget: { id: 3, timelineEventId: 3, caseInvestigationId },
      eventType: 'NewFeatureComparison' as TimelineEventType,
      eventSource: 'User',
      incidentRowId: 'fc-001',
    },
    {
      id: 4,
      message: 'Feature Comparison FC-003 was archived',
      actorUserId: 2,
      createdAt: new Date('2025-02-26T14:30:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 2, displayName: 'Isabella Rivera' },
      timelineEventTarget: { id: 4, timelineEventId: 4, caseInvestigationId },
      eventType: 'ArchivedFeatureComparison' as TimelineEventType,
      eventSource: 'User',
      incidentRowId: 'fc-003',
    },
    {
      id: 5,
      message: 'New comment added: "@BrooklynB I changed the status as you requested, let me know if you need anything else."',
      actorUserId: 3,
      createdAt: new Date('2025-02-12T12:53:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 3, displayName: 'Priscilla Barack' },
      timelineEventTarget: { id: 5, timelineEventId: 5, caseInvestigationId },
      eventType: 'NewComment' as TimelineEventType,
      eventSource: 'User',
    },
    {
      id: 6,
      message: 'Incident Status Changed to: Pending Response',
      actorUserId: 3,
      createdAt: new Date('2025-02-12T23:58:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 3, displayName: 'Priscilla Barack' },
      timelineEventTarget: { id: 6, timelineEventId: 6, caseInvestigationId },
      eventType: 'StatusChange' as TimelineEventType,
      eventSource: 'User',
    },
  ];

  return [...events].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

